<template>
  <div>
    <button
      class="btn"
      @click="toggleAudio"
    >
      <component
        :is="isPlaying ? IconPlayerPauseFilled : IconPlayerPlayFilled"
        :size="15"
        :class="isPlaying ? 'pause-icon' : 'play-icon'"
      />
    </button>
    <!-- Hidden audio element to manage playback -->
    <audio
      ref="audioRef"
      @play="handlePlay"
      @pause="handlePause"
      @ended="handlePause"
    >
      <source
        :src="props.audioUrl"
        type="audio/wav"
      />
    </audio>
  </div>
</template>

<script lang="ts" setup>
  import { ref, watch, onUnmounted } from 'vue'
  import { IconPlayerPlayFilled, IconPlayerPauseFilled } from '@tabler/icons-vue'

  interface Props {
    audioUrl: string
    startTime: string
    endTime: string
  }

  const props = defineProps<Props>()

  const audioRef = ref<HTMLAudioElement | null>(null)
  const timeoutId = ref<number | null>(null)
  const isPlaying = ref(false)

  const toggleAudio = () => {
    if (!audioRef.value) return

    if (isPlaying.value) {
      audioRef.value.pause()
    } else {
      playAudio()
    }
  }

  const playAudio = () => {
    if (!audioRef.value) return

    const startSeconds = Math.max(0, parseFloat(props.startTime) - 1)
    const endSeconds = parseFloat(props.endTime) + 1

    audioRef.value.currentTime = startSeconds
    audioRef.value.play()

    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
    }

    const duration = (endSeconds - startSeconds) * 1000
    timeoutId.value = window.setTimeout(() => {
      audioRef.value?.pause()
    }, duration)
  }

  const handlePlay = () => {
    isPlaying.value = true
  }

  const handlePause = () => {
    isPlaying.value = false
  }

  watch(
    () => props.audioUrl,
    (newUrl) => {
      if (audioRef.value) {
        audioRef.value.src = newUrl
      }
    }
  )

  onUnmounted(() => {
    if (audioRef.value) {
      audioRef.value.pause()
      audioRef.value = null
    }
    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
    }
  })
</script>

<style scoped>
  .play-icon {
    color: #2fb344;
  }

  .pause-icon {
    color: #d63939;
  }
</style>
